import PropTypes from 'prop-types';
import { useState, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn.js';

// --- Variants Definitions ---

const inputVariants = cva(
  'text-tertiary-100 placeholder:text-tertiary-40 w-full transition-all focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:placeholder:text-gray-400',
  {
    variants: {
      variant: {
        default:
          'border-tertiary-20 focus:border-primary-100 focus:ring-primary-100 dark:focus:border-primary-100 dark:focus:ring-primary-100 rounded-lg border bg-white px-4 py-3 focus:ring-2 dark:border-gray-600 dark:bg-gray-700/50',
        minimal:
          'border-tertiary-20 focus:border-primary-100 dark:focus:border-primary-100 rounded-none border-x-0 border-t-0 border-b bg-transparent px-2 py-3 shadow-none focus:ring-0 dark:border-gray-700 dark:bg-transparent',
      },
      error: {
        true: '!focus:border-red-500 !border-red-500',
      },
      hasRightIcon: {
        true: 'pr-11',
      },
      hasLeftIcon: {
        true: 'pl-11',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        error: true,
        className: '!focus:ring-red-500',
      },
    ],
    defaultVariants: {
      variant: 'default',
      error: false,
      hasRightIcon: false,
      hasLeftIcon: false,
    },
  },
);

const iconVariants = cva(
  'text-tertiary-60 absolute top-1/2 flex -translate-y-1/2 items-center transition-colors dark:text-gray-400',
  {
    variants: {
      position: {
        left: 'left-4',
        right: 'right-4',
      },
      error: {
        true: '!text-red-500',
      },
    },
    defaultVariants: {
      position: 'right',
      error: false,
    },
  },
);

// --- Component ---

const Input = forwardRef(
  (
    {
      type = 'text',
      name,
      id,
      value,
      onChange,
      placeholder,
      label,
      error,
      helperText,
      disabled = false,
      required = false,
      icon,
      iconPosition = 'right',
      rightIcon,
      showPasswordToggle = false,
      variant = 'default',
      className,
      inputClassName,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const inputType = showPasswordToggle && showPassword ? 'text' : type;
    const hasRightIcon = icon && iconPosition === 'right';
    const hasLeftIcon =
      showPasswordToggle || rightIcon || (icon && iconPosition === 'left');

    return (
      <div className={cn('w-full', className)}>
        {label && (
          <label
            htmlFor={id || name}
            className="text-tertiary-80 mb-1.5 block text-sm font-medium transition-colors dark:text-gray-300"
          >
            {label}
            {required && <span className="mr-1 text-red-500">*</span>}
          </label>
        )}

        <div className="relative">
          <input
            ref={ref}
            type={inputType}
            id={id || name}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              inputVariants({
                variant,
                error: !!error,
                hasRightIcon,
                hasLeftIcon,
              }),
              inputClassName,
            )}
            {...props}
          />

          {icon && iconPosition === 'right' && (
            <>
              {typeof icon === 'string' ? (
                <i
                  className={cn(
                    icon,
                    iconVariants({
                      position: 'right',
                      error: !!error,
                    }),
                  )}
                />
              ) : (
                <div
                  className={iconVariants({
                    position: 'right',
                    error: !!error,
                  })}
                >
                  {icon}
                </div>
              )}
            </>
          )}

          {icon &&
            iconPosition === 'left' &&
            !showPasswordToggle &&
            !rightIcon && (
              <>
                {typeof icon === 'string' ? (
                  <i
                    className={cn(
                      icon,
                      iconVariants({
                        position: 'left',
                        error: !!error,
                      }),
                    )}
                  />
                ) : (
                  <div
                    className={iconVariants({
                      position: 'left',
                      error: !!error,
                    })}
                  >
                    {icon}
                  </div>
                )}
              </>
            )}

          {rightIcon && (
            <div className="absolute top-1/2 left-4 -translate-y-1/2">
              {rightIcon}
            </div>
          )}

          {showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-tertiary-60 hover:text-tertiary-80 absolute top-1/2 left-4 -translate-y-1/2 transition-colors dark:text-gray-400 dark:hover:text-gray-300"
              disabled={disabled}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        </div>

        {error && (
          <p className="mt-1.5 text-xs font-medium text-red-500">
            {typeof error === 'string'
              ? error
              : typeof error === 'object' && error.message
                ? error.message
                : String(error)}
          </p>
        )}

        {helperText && !error && (
          <p className="text-tertiary-60 mt-1.5 text-xs transition-colors dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

Input.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconPosition: PropTypes.oneOf(['left', 'right']),
  rightIcon: PropTypes.node,
  showPasswordToggle: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'minimal']),
  className: PropTypes.string,
  inputClassName: PropTypes.string,
};

export default Input;
